/** In-app notification endpoints (`/notifications`), scoped to the current user. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getData, getList, patchData } from "../lib/apiClient";
import { queryKeys } from "./queryKeys";
import { AppNotification } from "../types/models";

export interface ListNotificationsParams {
  page?: number;
  limit?: number;
}

export const notificationsApi = {
  list: (params?: ListNotificationsParams) =>
    getList<AppNotification>("/notifications", params),
  unreadCount: () => getData<{ count: number }>("/notifications/unread-count"),
  markRead: (id: string) => patchData<null>(`/notifications/${id}/read`),
  markAllRead: () => patchData<{ updated: number }>("/notifications/read-all"),
};

// ── Hooks ────────────────────────────────────────────────────────────

export const useNotifications = (params?: ListNotificationsParams) =>
  useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => notificationsApi.list(params),
  });

/**
 * Unread badge count, polled in the background so the bell stays current
 * without a websocket on the client. `enabled` lets callers pause polling
 * while signed out.
 */
export const useUnreadCount = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: () => notificationsApi.unreadCount(),
    enabled,
    refetchInterval: enabled ? 60_000 : false,
    select: (d) => d.count,
  });

export const useMarkNotificationRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all }),
  });
};

export const useMarkAllNotificationsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: queryKeys.notifications.all }),
  });
};
